import React from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";
import { Button } from "@vds/buttons";
import { Body, Title, Micro } from "@vds/typography";
import {
  MarginSpacerS,
  MarginSpacerL,
  MarginSpacerXL,
  } from "common/components/styleTag";
import { CollapsibleList } from "common/components";
import { formatPriceString } from "utils/commonUtils";
import BroadbandPlan  from "common/components/FWA/PlanSelection/BroadbandPlan";

const BusinessPlanCard = ({
  planDetail,
  autoPayDiscount,
  noOfLines,
  isEnabled,
  onSelect,
  tierPrices,
  aemFeatures,
  isRecommended,
  promoSpacing = false,
  maxEligibility,
  fromFeature = false,
  buttonTextOverride,
  useTierPrices,
  totalNumberOfLines,
  packages = null,
  newMixedCartContent=false,
  isLandingFromInternetWithMobile,
  isJOBundle,
  ...props
}) => {
  const { t } = useTranslation(["plans"]);
  const { displayName, monthlyFee, promoEligible, name, retailPrice } =
    planDetail || {};
  const isPromoEligible = props?.isPromoEligible || promoEligible;
  const isMobile = useMediaQuery({
    query: "(max-width: 545px)",
  });

  const getPrice = () => {
    let finalPrice;
    if (totalNumberOfLines < 6) {
      const checkPrice = tierPrices && tierPrices[totalNumberOfLines];
      finalPrice = useTierPrices && totalNumberOfLines
        ? Number(checkPrice) + Number(autoPayDiscount)
        : name && name
          ? parseInt(retailPrice) == parseFloat(retailPrice)
            ? retailPrice
            : Number(retailPrice)?.toFixed(2)
          : Number.parseInt(monthlyFee);
    } else {
      finalPrice = useTierPrices && totalNumberOfLines
        ? Number(tierPrices[5]) + Number(autoPayDiscount)
        : name && name
          ? parseInt(retailPrice) == parseFloat(retailPrice)
            ? retailPrice
            : Number(retailPrice)?.toFixed(2)
          : Number.parseInt(monthlyFee);
    }

    return finalPrice

  };

  const getFinalPrice = () => {
    let price = getPrice();
    if (autoPayDiscount) {
      const discount = Number.parseInt(autoPayDiscount);
      price -= discount;
    }
    return price;
  };

  return (
    <>
      <TileContainer
        height={props?.height}
        minHeight={props?.minHeight}
        fromFeature={fromFeature}
        isPromoEligible={isPromoEligible}
        promoSpacing={promoSpacing}
        noOfLines={(props?.fromPlansNew ? props?.selected : noOfLines && noOfLines)}
        totalNumberOfLines={packages ? Object.keys(packages)?.length : 1}
      >
        {!!isPromoEligible && (
          <PromoBadge>
            <Body size="small" bold color="#FFFFFF">
              {t("plans.aggregate_plans.tradeInPromoMsg", { ns: "plans" })}
            </Body>
          </PromoBadge>
        )}
        <TileContents>
          {isRecommended ? (
            <>
              <Body size="small" color="#EE0000">
                {t("plans.aggregate_plans.recommendedText", { ns: "plans" })}
              </Body>
              <MarginSpacerS />
            </>
          ) : (
            <MarginSpacerL />
          )}
          <TopSection className="top-section" minHeight={props.minHeightTitle} height={props?.height}>
            <Title size={isMobile ? "large" : "medium"}>
              {displayName ? displayName : name}
            </Title>
            {name && (
              <Body size="small">
                {planDetail.maxEligibility === null
                  ? "Single device"
                  : ''
                  }
              </Body>
            )}
          </TopSection>
          {!props?.selected && <Button
            use="secondary"
            size="large"
            width="100%"
            disabled={!isEnabled}
            onClick={() => { onSelect({ billingCode: planDetail.billingCode, finalPrice: getFinalPrice() }) }}
            data-track={props?.fromPlansNew && !fromFeature && props?.selected ? "Keep this plan" :
            props?.fromPlansNew && fromFeature && props?.selected ? t("features.device-protection.keep_protection_plan", { ns: "features" }) :
              buttonTextOverride ? buttonTextOverride : aemFeatures?.["buttonText"] ? aemFeatures["buttonText"] :
                fromFeature ? t("features.device-protection.add_protection_plan", { ns: "features" }) : t("plans.aggregate_plans.select_plan", { ns: "plans" })}
            style = {{ color: isJOBundle && buttonTextOverride == "Selected" ? "rgb(0,119,180)" : "black" }} 
          >
            {props?.fromPlansNew && !fromFeature && props?.selected ? "Keep this plan" :
              props?.fromPlansNew && fromFeature && props?.selected ? t("plans.keep_protection_plan", { ns: "plans" }) :
                buttonTextOverride ? buttonTextOverride : aemFeatures?.["buttonText"] ? aemFeatures["buttonText"] :
                  fromFeature ? t("plans.add_protection_plan", { ns: "plans" }) : t("plans.aggregate_plans.select_plan", { ns: "plans" })}
          </Button>}
          {newMixedCartContent ? <></> : props?.fromPlansNew && props?.selected ?
            <AppliedToLines>
              <Body
                size="large"
                color="#0077B4"
                bold
              > { fromFeature ? "You added this protection" : "You selected this plan" }
              </Body>
            </AppliedToLines> :
            noOfLines && !isLandingFromInternetWithMobile ? (
              <AppliedToLines>
                <Body
                  size="small"
                  color="#0077B4"
                  bold
                >{`Applied to ${noOfLines} ${noOfLines > 1 ? "lines" : "line"
                  }`}
                  </Body>
              </AppliedToLines>
              
            ) : (
              <div style={{ marginBottom: "2.5rem" }} />
            )}
            <MarginSpacerL />
          <PriceLockup name={name}>
            <Title size="large" bold primitive="h1">
              {`${formatPriceString(getFinalPrice(), true)}`}
            </Title>
            <div style={{ maxWidth: "166px" }}>
              <Body bold={name ? true : false} size={name ? "large" : "small"}>
                { fromFeature ?
                  (planDetail.maxEligibility === null ?
                  t("plans.aggregate_plans.perLine", { ns: "plans" })
                  :
                  t("plans.aggregate_plans.perMonth", { ns: "plans" })
                ) : t("plans.aggregate_plans.perLine", { ns: "plans" })}
                
              </Body>
              {!name && (
                <Body size="small">
                  {t("plans.aggregate_plans.plusTax", { ns: "plans" })}
                </Body>
              )}
            </div>
          </PriceLockup>
          {!!autoPayDiscount && (
            <div style={{ marginTop: "0.75rem", height: '3vh' }}>
              <Body size="small">
                {`${formatPriceString(getPrice(), true)} ${t(
                  "plans.buePlansCard.withoutAutoPay",
                  { ns: "plans" }
                )}`}
              </Body>
            </div>
          )}
          <MarginSpacerXL />
          {!!tierPrices && !newMixedCartContent && !isLandingFromInternetWithMobile && (
            <>
              <PriceTierGrid>
                {Object.entries(tierPrices)?.map(([key, value]) => (
                  <>
                    <PriceTierPrice>{`${formatPriceString(value, true)}`}</PriceTierPrice>
                    <PriceTierLines>{`${key}${key == 5 ? "+" : ""} ${t(
                      "plans.buePlansCard.lines",
                      { count: Number(key), ns: "plans" }
                    )}`}</PriceTierLines>
                  </>
                ))}
              </PriceTierGrid>
              <MarginSpacerL />
            </>
          )}
          {newMixedCartContent && <MarginSpacerL />}
          <ItemWrapper>
            {!!aemFeatures && (
              <CollapsibleList
                heading={aemFeatures?.heading}
                items={aemFeatures?.features || []}
                gap="0.75rem"
              />
            )}
            {!!aemFeatures && !!aemFeatures?.discountHeading && (
              <>
                <MarginSpacerL />
                <CollapsibleList
                  heading={aemFeatures?.discountHeading}
                  items={aemFeatures?.discountFeatures || []}
                  gap="0.75rem"
                />
              </>
            )}
            {!!aemFeatures?.disclaimers?.length && (
              <>
                <MarginSpacerL />
                {aemFeatures.disclaimers.map((item) => (
                  <DisclaimerItem key={item}>
                    <Micro>{item}</Micro>
                  </DisclaimerItem>
                ))}
              </>
            )}

          </ItemWrapper>
          <MarginSpacerS />
           <BroadbandPlan planId={planDetail?.billingCode} flow="nse" />
        </TileContents>
      </TileContainer>
    </>
  );
};

const TileContainer = styled.div`
  min-width: 290px;
  height:${({ height }) => height ? height : 'auto'};
  min-height: ${({ isPromoEligible }) => (isPromoEligible ? "600px" : "auto")};
  flex-shrink: 0;
  border-radius: 8px;
  border: ${(props) =>
    props.noOfLines
      ? `2px solid var(--vds-elements-text-icons-borders-lowcontrast-onlight, #000)`
      : `1px solid var(--vds-elements-text-icons-borders-lowcontrast-onlight, #d8dada)`};

  background: #fff;
  padding: 1rem 0 1.5rem 0;
  padding-top: ${(props) => (props?.isPromoEligible ? "0" : "1rem")};
  margin-top: ${(props) =>
    props?.promoSpacing && !props?.isPromoEligible ? "2.5rem" : "0"};
  @media (min-width: 301px) and (max-width: 545px) {
    margin-top: 0;
    min-width: 252px;
    max-width: 100%;
    // min-width: 220px;
  }
  @media (max-width: 300px) {
    min-width: 190px    
  }
  @media (max-width: 500px) {
    min-height: ${({ isPromoEligible, minHeight }) => (minHeight&&isPromoEligible ? ((minHeight + 40)+'px') : minHeight ? (minHeight+'px') : isPromoEligible ? "600px" : "auto")};
    margin-top: ${(props) => !props?.isPromoEligible&&!props.fromFeature ? "2.5rem" : "0"};
  }
`;

const PromoBadge = styled.div`
  height: 2.5rem;
  border-radius: 8px 8px 0px 0px;
  background: #000;
  width: 100%;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
`;

const TileContents = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 0.75rem;
  &:last-child {
    ul {
      li {
        &:before {
          margin-top: -2.5px;
        }
      }
    }
  }
`;

const TopSection = styled.div`
min-height : ${({minHeight})=>minHeight ? minHeight : ''};
margin-bottom:${({minHeight})=>minHeight ? '' : '24px !important;'};
  h4 {
    padding-bottom: 4px;
    line-height: 24px;
  }
  p {
    margin-bottom: 0px;
  }
  @media screen and (max-width:500px){
    min-height : ${({minHeight,height})=> height  && minHeight  ? '130px' : minHeight ?  '115px' : ''};
  }
`;

const PriceLockup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: ${({ name }) => (name ? "baseline" : "")};
  gap: 0.5rem;
  > h1 {
    margin-top: -6px;
  }
`;

const AppliedToLines = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 3.5rem;
  display: flex;
  justify-content: center;
`;

const PriceTierGrid = styled.div`
  display: grid;
  grid-template-columns: [price] 1.625rem [noOfLines] auto;
  row-gap: 0.25rem;
  column-gap: 1rem;
`;

const PriceTierPrice = styled.div`
  grid-column-start: price;
`;

const PriceTierLines = styled.div`
  grid-column-start: noOfLines;
`;

const DisclaimerItem = styled.div`
  padding-left: 1.2rem;
`;
const ItemWrapper = styled.div`
 min-height:380px
`;
export default BusinessPlanCard;
